import { type Row } from "@tanstack/react-table";
import { twMerge } from "tailwind-merge";
import { TableCell } from "./TableCell";
import { memo, MouseEventHandler, useCallback } from "react";

interface TableRowProps<TData> {
  row: Row<TData>;
  rowClassName?: (row: Row<TData>) => string;
  onRowClick?: (row: Row<TData>) => void;
  loading?: boolean;
  testId?: string;
}

function TableRowComponent<TData>({
  row,
  rowClassName,
  onRowClick,
  loading,
  testId,
}: TableRowProps<TData>) {
  const handleClick: MouseEventHandler<HTMLTableRowElement> = useCallback(
    (event) => {
      if (loading) return;
      const target = event.target as HTMLElement;
      if (target.closest("button, a, input, select, textarea")) return;
      onRowClick?.(row);
    },
    [row, onRowClick, loading],
  );

  return (
    <tr
      onClick={handleClick}
      data-testid={testId}
      className={twMerge(
        "border-b border-b-[#F2F4F7] bg-white transition-colors duration-150 hover:bg-[#F7FAFE] [&:has(button:hover)]:bg-white",
        onRowClick && !loading && "cursor-pointer",
        rowClassName?.(row),
      )}
    >
      {row.getVisibleCells().map((cell) => (
        <TableCell key={cell.id} cell={cell} loading={loading} />
      ))}
    </tr>
  );
}

export const TableRow = memo(TableRowComponent) as typeof TableRowComponent;
